/**
 * Item feedback — doc-style comments on TEND entities.
 *
 * Gateway-owned endpoints (/api/feedback, not proxied). The entity is
 * addressed by entity_type + entity_key strings so the same API serves
 * every Phase-2 surface (templates, tactics, queues, sections).
 */
import { API_BASE_URL } from '../config/api';
import { authedFetch } from '../utils/authedFetch';

const BASE = `${API_BASE_URL}/feedback`;

const jsonHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

export type FeedbackEntityType =
  | 'tendv2_content'
  | 'channel_queue'
  | 'overlay_template'
  | 'tactic'
  | 'analytics_channel'
  | 'design_section'
  | 'editorial_section';

export interface FeedbackAuthor {
  id?: string;
  name: string;
  initials: string;
  /** 'SA' | 'CS' | the brand's name — resolved server-side. */
  badge: string;
}

export interface FeedbackComment {
  id: number;
  entity_type: FeedbackEntityType;
  entity_key: string;
  brand_id: number;
  body: string;
  created_at: string | null;
  edited_at?: string | null;
  author: FeedbackAuthor;
  is_own: boolean;
}

export interface FeedbackThreadState {
  resolved: boolean;
  resolved_at?: string;
  resolved_by?: string | null;
}

export interface FeedbackCountEntry {
  count: number;
  resolved: boolean;
}

export async function getFeedback(
  entityType: FeedbackEntityType,
  entityKey: string,
  brandId: number
): Promise<{ feedback: FeedbackComment[]; thread: FeedbackThreadState }> {
  const params = new URLSearchParams({
    entity_type: entityType,
    entity_key: entityKey,
    brand_id: String(brandId),
  });
  const res = await authedFetch(`${BASE}?${params}`, { headers: jsonHeaders() });
  if (!res.ok) throw new Error(`Failed to load feedback (${res.status})`);
  const data = await res.json();
  return { feedback: data.feedback || [], thread: data.thread || { resolved: false } };
}

export async function editFeedback(feedbackId: number, body: string): Promise<FeedbackComment> {
  const res = await authedFetch(`${BASE}/${feedbackId}`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify({ body }),
  });
  if (!res.ok) throw new Error(`Failed to edit feedback (${res.status})`);
  return await res.json();
}

export async function setThreadResolved(
  entityType: FeedbackEntityType,
  entityKey: string,
  brandId: number,
  resolved: boolean
): Promise<FeedbackThreadState> {
  const res = await authedFetch(`${BASE}/${resolved ? 'resolve' : 'reopen'}`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ entity_type: entityType, entity_key: entityKey, brand_id: brandId }),
  });
  if (!res.ok) throw new Error(`Failed to update thread (${res.status})`);
  return (await res.json()).thread;
}

export async function postFeedback(
  entityType: FeedbackEntityType,
  entityKey: string,
  brandId: number,
  body: string
): Promise<FeedbackComment> {
  const res = await authedFetch(BASE, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ entity_type: entityType, entity_key: entityKey, brand_id: brandId, body }),
  });
  if (!res.ok) throw new Error(`Failed to post feedback (${res.status})`);
  return await res.json();
}

export async function deleteFeedback(feedbackId: number): Promise<boolean> {
  const res = await authedFetch(`${BASE}/${feedbackId}`, {
    method: 'DELETE',
    headers: jsonHeaders(),
  });
  return res.ok;
}

export async function getFeedbackCounts(
  entityType: FeedbackEntityType,
  entityKeys: string[],
  brandId: number
): Promise<Record<string, FeedbackCountEntry>> {
  if (entityKeys.length === 0) return {};
  const params = new URLSearchParams({
    entity_type: entityType,
    entity_keys: entityKeys.join(','),
    brand_id: String(brandId),
  });
  const res = await authedFetch(`${BASE}/counts?${params}`, { headers: jsonHeaders() });
  if (!res.ok) return {};
  const data = await res.json();
  return data.counts || {};
}
